'use client';

import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { POItemsTable } from '@/components/purchase/POItemsTable';
import { ItemSelector } from '@/components/inventory/ItemSelector';
import type { POItemFormState } from '@/types/purchase-order-form';
import { formatCurrency } from '@/lib/utils';

interface POItemsTabProps {
  items: POItemFormState[];
  onItemsChange: (items: POItemFormState[]) => void;
  hospitalId: string;
  disabled?: boolean;
}

export function POItemsTab({ items, onItemsChange, hospitalId }: POItemsTabProps) {
  const [isItemSelectorOpen, setIsItemSelectorOpen] = useState(false);

  const totalAmount = items.reduce((sum, item) => sum + item.line_total, 0);

  return (
    <div className="space-y-4">
      <Tabs defaultValue="items" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="items">Line Items ({items.length})</TabsTrigger>
          <TabsTrigger value="add">Add Items</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="space-y-4">
          <POItemsTable items={items} onItemsChange={onItemsChange} />
        </TabsContent>

        <TabsContent value="add">
          <div className="space-y-4">
            <ItemSelector
              open={isItemSelectorOpen}
              onOpenChange={setIsItemSelectorOpen}
              onSelect={(newItem) => {
                onItemsChange([...items, newItem]);
                setIsItemSelectorOpen(false);
              }}
              hospitalId={hospitalId}
            />
            {items.length > 0 && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Item Count</p>
                    <p className="text-2xl font-bold">{items.length}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Total Amount</p>
                    <p className="text-2xl font-bold font-mono text-primary">
                      {formatCurrency(totalAmount)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
